import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Loader2, FileText } from "lucide-react";

const MONTHS3 = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const LBL = { fica: "FICA / Medicare", futa: "FUTA", suta: "SUTA", workers_comp: "Workers' Comp", general_liability: "General Liability" };

// Resolve the burden lines that actually applied to this bid, and whether they came
// from a bid-specific override or the company default.
function resolveBurden(bid, brand) {
  const bidLb = bid?.labor_burden;
  const asLines = (lb) => {
    if (!lb) return null;
    if (lb.lines) return lb.lines.map((x) => ({ name: x.name || "Burden", pct: Number(x.pct) || 0 }));
    if (lb.items) return Object.entries(lb.items).map(([k, v]) => ({ name: LBL[k] || k, pct: Number(v) || 0 }));
    if (lb.mode === "override") return [{ name: "Burden (override)", pct: Number(lb.override_pct) || 0 }];
    return null;
  };
  const bidLines = asLines(bidLb);
  if (bidLines) return { source: "bid", lines: bidLines };
  return { source: "company", lines: asLines(brand?.labor_burden) || [] };
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-sm text-slate-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

export default function BOEPrint() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [computed, setComputed] = useState(null);
  const [brand, setBrand] = useState(null);
  const [status, setStatus] = useState("loading");

  // Client-facing BOE print doc is always light — never inherit the app's dark mode.
  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains("dark");
    html.classList.remove("dark");
    return () => { if (wasDark) html.classList.add("dark"); };
  }, []);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/bids/${bidId}`),
      api.post(`/bids/${bidId}/compute`),
      api.get("/branding"),
    ]).then(([b, c, br]) => {
      setBid(b.data); setComputed(c.data); setBrand(br.data); setStatus("ready");
    }).catch(() => setStatus("error"));
  }, [bidId]);

  useEffect(() => {
    if (!brand) return;
    const fams = [brand.font_heading, brand.font_body].filter(Boolean);
    const id = "brand-fonts";
    let l = document.getElementById(id);
    if (!l) { l = document.createElement("link"); l.id = id; l.rel = "stylesheet"; document.head.appendChild(l); }
    l.href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${f.replace(/ /g, "+")}:wght@400;600;700`).join("&")}&display=swap`;
  }, [brand]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/bids/${bidId}/boe/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(bid?.name || "Estimate").replace(/[^A-Za-z0-9._-]+/g, "_")}_Basis_of_Estimate.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      const { toast } = await import("sonner");
      toast.error("PDF render failed — use Print as a fallback.");
    } finally { setPdfBusy(false); }
  };

  if (status !== "ready") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-500">
        {status === "error" ? <FileText className="w-6 h-6 text-slate-300" /> : <Loader2 className="w-6 h-6 animate-spin text-blue-600" />}
        <div className="text-sm">{status === "error" ? "Could not load this estimate." : "Building the Basis of Estimate…"}</div>
        <Button variant="outline" onClick={() => navigate(`/bids/${bidId}`)}><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to bid</Button>
      </div>
    );
  }

  const b = brand || {};
  const primary = b.primary || "#003E87";
  const ink = b.ink || "#0F172A";
  const company = b.company_name || "";
  const headFont = b.font_heading || "Space Grotesk";
  const bodyFont = b.font_body || "Inter";

  const totals = computed.totals || {};
  const groups = computed.groups || {};
  const groupNames = Object.keys(groups);
  const perW = totals.cost_per_watt ? `$${Number(totals.cost_per_watt).toFixed(4)} / Watt` : "—";
  const burden = resolveBurden(bid, brand);
  const burdenTotal = burden.lines.reduce((s, x) => s + (Number(x.pct) || 0), 0);
  const wx = bid?.location_profile?.weather || null;
  const wxFactor = bid?.location_profile?.weather_blended_factor;
  const workWeek = b.work_week_days || 5;
  const otf = groupNames.length ? (groups[groupNames[0]].ot_factor || 1) : 1;
  const hpd = groupNames.length ? (groups[groupNames[0]].hours_per_day || 10) : 10;
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const bondPct = bid?.bond_pct != null && bid?.bond_pct !== "" ? Number(bid.bond_pct) : Number(b.bond_rate_pct || 0);

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: `'${bodyFont}',sans-serif` }} data-testid="boe-print-page">
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/bids/${bidId}`)}><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
        <div className="text-sm text-slate-500">Basis of Estimate · deterministic audit document (no AI-generated content)</div>
        <div className="flex-1" />
        <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy} data-testid="boe-server-pdf">
          {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} {pdfBusy ? "Rendering…" : "Export PDF"}
        </Button>
        <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => window.print()} data-testid="boe-print-btn">
          <Download className="w-4 h-4 mr-1.5" /> Print
        </Button>
      </div>

      <style>{`
        .boe-doc .font-display{font-family:'${headFont}',sans-serif}
        .boe-block{break-inside:avoid;page-break-inside:avoid}
        @media print { @page { size: Letter; margin: 0.5in; } .no-print{display:none!important} body{background:#fff} }
      `}</style>

      <div className="max-w-[8in] mx-auto my-6 bg-white shadow-lg boe-doc" data-testid="boe-print-doc">
        <div className="p-10">
          {/* ── Header ── */}
          <div className="boe-block" style={{ borderBottom: `3px solid ${primary}`, paddingBottom: 16, marginBottom: 20 }}>
            {b.logo
              ? <img src={b.logo} alt="" style={{ maxHeight: 42, marginBottom: 8 }} />
              : <div style={{ color: primary, fontWeight: 700, letterSpacing: 1, fontSize: 13 }}>{company.toUpperCase()}</div>}
            <div className="font-display" style={{ color: ink, fontSize: 34, fontWeight: 700, lineHeight: 1.05, marginTop: 8 }}>Basis of Estimate</div>
            <div className="text-slate-500 text-sm mt-1">{bid?.name || "Untitled estimate"}</div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6 boe-block">
            <Field label="Client" value={bid?.client_name || "—"} />
            <Field label="Location" value={bid?.location || bid?.state || "—"} />
            <Field label="Prepared" value={today} />
            <Field label="System size" value={bid?.capacity_mw ? `${num(bid.capacity_mw)} MWdc` : "—"} mono />
            <Field label="Fully loaded price" value={money(totals.total)} mono />
            <Field label="$ / Watt" value={perW} mono />
          </div>

          <p className="text-[11px] text-slate-500 leading-relaxed mb-6 boe-block">
            This Basis of Estimate documents how every labor rate and productivity factor in this estimate was built.
            All figures are <b>deterministic</b> — computed directly from the priced estimate. Nothing here is AI-generated.
          </p>

          {/* ── Global assumptions ── */}
          <div className="boe-block mb-6">
            <div className="font-display text-lg font-semibold mb-3" style={{ color: primary }}>Global Assumptions</div>

            <div className="grid grid-cols-2 gap-4">
              {/* Labor burden */}
              <div className="rounded-lg border border-slate-200 p-4" data-testid="boe-burden">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-700">Labor burden</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${burden.source === "bid" ? "bg-[#F4C400]/20 text-[#7a6400]" : "bg-slate-100 text-slate-500"}`}>
                    {burden.source === "bid" ? "Bid-specific override" : "Company default"}
                  </span>
                </div>
                <div className="text-[10px] text-slate-400 mb-1.5">Applied to base wage only; prevailing-wage fringes pass through untouched.</div>
                <table className="w-full text-xs">
                  <tbody>
                    {burden.lines.map((x, i) => (
                      <tr key={i} className="border-t border-slate-50" data-testid={`boe-burden-line-${i}`}>
                        <td className="py-1 text-slate-600">{x.name}</td>
                        <td className="py-1 text-right font-mono text-slate-800">{Number(x.pct).toFixed(2)}%</td>
                      </tr>
                    ))}
                    {burden.lines.length === 0 && <tr><td className="py-1 text-slate-400 italic">No burden configured (0%)</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200">
                      <td className="pt-1.5 text-slate-500 font-medium">Total on base wage</td>
                      <td className="pt-1.5 text-right font-mono font-bold" style={{ color: ink }} data-testid="boe-burden-total">{burdenTotal.toFixed(2)}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Overtime + bond */}
              <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="boe-ot-bond">
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1">Overtime factor</div>
                  <div className="font-mono text-sm text-slate-900">×{Number(otf).toFixed(2)}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Auto-derived from the work week: {hpd}h × {workWeek}d ({num(hpd * workWeek)}h/wk, FLSA time-and-a-half over 40h).</div>
                </div>
                <div className="border-t border-slate-100 pt-2">
                  <div className="text-xs font-semibold text-slate-700 mb-1">Bond rate <span className="text-[10px] font-normal text-slate-400">· project-specific</span></div>
                  <div className="font-mono text-sm text-slate-900">{bondPct ? `${num(bondPct)}%` : "No bond on this bid"}</div>
                  {bondPct ? <div className="text-[10px] text-slate-400 mt-0.5">= {money(totals.bond_amount)} on the marked-up price.</div> : null}
                </div>
              </div>
            </div>

            {/* Weather model */}
            <div className="rounded-lg border border-slate-200 p-4 mt-4 boe-block" data-testid="boe-weather">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold text-slate-700">Weather &amp; productivity source</div>
                {wxFactor != null && <span className="text-[10px] text-slate-500">Blended weather factor <b className="font-mono text-slate-800">{Math.round(wxFactor * 100)}%</b></span>}
              </div>
              {wx?.months?.length ? (
                <>
                  <div className="text-[10px] text-slate-400 mb-2">Expected field-days lost per month{wx.county ? ` · ${wx.county}` : ""}{wx.years ? ` · ${wx.years}-yr normals` : ""}. This drives each scope's productivity pace (weather never double-counts in the calendar).</div>
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="text-slate-400">{wx.months.map((m) => <th key={m.month} className="font-normal py-0.5">{MONTHS3[m.month - 1]}</th>)}</tr>
                    </thead>
                    <tbody>
                      <tr className="font-mono text-slate-700">{wx.months.map((m) => <td key={m.month} className="text-center py-0.5" data-testid={`boe-weather-${m.month}`}>{(m.lost_days || 0).toFixed(1)}</td>)}</tr>
                    </tbody>
                  </table>
                  {wx.source && <div className="text-[9px] text-slate-400 mt-1.5">{wx.source}</div>}
                </>
              ) : (
                <div className="text-[10px] text-slate-400">No site weather model — set the project's state &amp; county under Bid setup → Location. A 100% weather factor (no delay) is assumed.</div>
              )}
            </div>
          </div>

          {/* ── Per-scope rate build-ups ── */}
          <div className="font-display text-lg font-semibold mb-3" style={{ color: primary }}>Scope Rate Build-Ups</div>
          {groupNames.length === 0 && <div className="text-sm text-slate-400 italic">No crews have been built yet — build a crew on a scope in the Estimate grid to populate rate build-ups.</div>}

          {groupNames.map((name) => {
            const e = groups[name];
            const slow = Number(e.slowdown_pct) || 0;
            const wf = e.weather_factor != null ? e.weather_factor : 1;
            const prod = e.productivity != null ? e.productivity : 1;
            return (
              <div key={name} className="rounded-lg border border-slate-200 p-4 mb-4 boe-block" data-testid={`boe-scope-${name}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold" style={{ color: ink }}>{name}</div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span>Headcount <b className="text-slate-800">{e.headcount}</b></span>
                    <span>Effective <b className="text-slate-800 font-mono">${num(e.effective_per_manhour)}/hr</b></span>
                    {e.days != null && <span>Duration <b className="text-slate-800">{num(e.days)}d</b></span>}
                  </div>
                </div>

                {/* Rate build table */}
                <table className="w-full text-xs mb-2">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                      <th className="text-left font-medium py-1">Role</th>
                      <th className="text-right font-medium py-1">Count</th>
                      <th className="text-right font-medium py-1">Base $/hr</th>
                      <th className="text-right font-medium py-1">Fringe $/hr</th>
                      <th className="text-right font-medium py-1">Burdened $/hr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(e.rows || []).map((r, i) => (
                      <tr key={i} className="border-b border-slate-50" data-testid={`boe-crew-row-${name}-${i}`}>
                        <td className="py-1 text-slate-700">{r.role}</td>
                        <td className="py-1 text-right font-mono text-slate-600">{num(r.count)}</td>
                        <td className="py-1 text-right font-mono text-slate-600">${num(r.base_rate)}</td>
                        <td className="py-1 text-right font-mono text-slate-600">${num(r.fringe)}</td>
                        <td className="py-1 text-right font-mono text-slate-800">${num(r.burdened_hourly)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="rounded bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-600 font-mono space-y-1">
                  <div>base $/hr × {Number(otf).toFixed(2)} OT × {(1 + (Number(e.burden_pct) || 0) / 100).toFixed(3)} burden ({num(e.burden_pct)}%) + fringe (flat) = <b className="text-slate-900">blended ${num(e.blended_per_manhour)}/hr</b></div>
                  {slow > 0 && <div>blended ${num(e.blended_per_manhour)}/hr × {(1 + slow / 100).toFixed(2)} slowdown ({slow}%) = <b className="text-slate-900">effective ${num(e.effective_per_manhour)}/hr</b></div>}
                  <div>productivity {Math.round(prod * 100)}% = weather {Math.round(wf * 100)}% × (1 − slowdown {slow}%)</div>
                  {e.production_rate ? <div>schedule = {num(e.driver_qty)} {e.production_unit} ÷ ({num(e.production_rate)} {e.production_unit}/day × {Math.round(prod * 100)}%) = <b className="text-slate-900">{num(e.days)} work-days</b></div> : null}
                </div>

                {(e.equipment_rows || []).length > 0 && (
                  <div className="mt-3" data-testid={`boe-equip-${name}`}>
                    <div className="text-[11px] font-semibold text-slate-700 mb-1">Equipment (priced over {num(e.days)} work-days)</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                          <th className="text-left font-medium py-1">Item</th>
                          <th className="text-right font-medium py-1">Qty</th>
                          <th className="text-right font-medium py-1">$/hr</th>
                          <th className="text-right font-medium py-1">Hours</th>
                          <th className="text-right font-medium py-1">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {e.equipment_rows.map((r, i) => (
                          <tr key={i} className="border-b border-slate-50" data-testid={`boe-equip-row-${name}-${i}`}>
                            <td className="py-1 text-slate-700">{r.name}</td>
                            <td className="py-1 text-right font-mono text-slate-600">{num(r.qty)}</td>
                            <td className="py-1 text-right font-mono text-slate-600">${num(r.hourly)}</td>
                            <td className="py-1 text-right font-mono text-slate-600">{num(r.hours)}</td>
                            <td className="py-1 text-right font-mono text-slate-800">{money(r.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {e.equipment_rows[0]?.derivation && (
                      <div className="text-[10px] text-slate-400 mt-1 font-mono">{e.equipment_rows[0].derivation.ownership} · {e.equipment_rows[0].derivation.operating}</div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
                  <span>Labor cost <b className="text-slate-800 font-mono">{money(e.labor_cost)}</b></span>
                  <span>Man-hours <b className="text-slate-800 font-mono">{num(e.man_hours)}</b></span>
                  {e.crews ? <span>Crews <b className="text-slate-800 font-mono">{num(e.crews)}</b></span> : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
